import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  IconButton,
  Tooltip
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Refresh } from '@mui/icons-material'
import { useApi } from '../../../hooks/useApi'
import { ApiResponse } from '../../../typings/ApiResponse'
import { getInboundAdjustSku, postDeclareExcess } from '../config/api'
import { Loading } from '../../../components/UI'
import { Status } from '../../../hooks/useFetchState'
import { usePagination } from '../../../hooks/usePagination'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { Pagination } from '../../../components/Pagination'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal, ModalReturnType } from '../../../hooks/useModal'
import { AsnAdjustSkuUpdate } from '../../../interfaces/Inbound/InboundAdjust'
import { useUser } from '../../../hooks/useUser'
import { WorkOrderInboundSkuAdjust } from './Entities/InboundAdjust'
import { StatusIcon } from '../../../components/_WMS/StatusIcon/StatusIcon'

interface ASNExcessModalProps {
  excessModalState: ModalReturnType<{
    workOrderId: number
  }>
  handleSnackbar: (message: string, success: boolean) => void
}

export const ASNExcessModal: React.FC<ASNExcessModalProps> = ({
  excessModalState,
  handleSnackbar
}) => {
  const classes = useStyles()
  const [errors, setErrors] = React.useState<Record<string, boolean>>({})

  const [loading, setLoading] = useState<boolean>(false)
  const { state, callApi } = useApi<ApiResponse<WorkOrderInboundSkuAdjust[]>>()
  const pagination = usePagination(5)
  const {
    userDataState: { userData }
  } = useUser()

  const [items, setItems] = useState<WorkOrderInboundSkuAdjust[]>([])

  const [quantities, setQuantities] = useState<{ [key: string]: number }>({})

  const [addedItems, setAddedItems] = useState<WorkOrderInboundSkuAdjust[]>([])
  const [filterSkuId, setFilterSkuId] = useState<number>(0)
  const [filterBatchId, setFilterBatchId] = useState<number>(0)
  const [filterSku, setFilterSku] = useState<string>('')
  const [filterBatch, setFilterBatch] = useState<string>('')
  const workOrderId = excessModalState.extendedProps?.workOrderId || 0

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setItems([])

    requestGetInboundAdjust(auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const requestGetInboundAdjust = (
    page?: number,
    pageSize?: number,
    filters?: Filter[]
  ) => {
    setLoading(true)
    callApi(getInboundAdjustSku(workOrderId, page, pageSize, filters, true)) // true: flag para obtener solo los registros que no tengan faltantes
      .then((data) => {
        setItems(data.items as WorkOrderInboundSkuAdjust[])
        setLoading(false)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
    setLoading(false)
  }

  const mapSingleItemToPayload = (rowIndex: number): AsnAdjustSkuUpdate => {
    const item = addedItems[rowIndex]
    return {
      workOrderId: workOrderId,
      skuId: item.skuId,
      salePositions: [],
      asnDetailId: item.asnDetailId,
      reductionQty: null,
      missingQty: null,
      excessQty: item.excessQty || 0,
      userId: userData?.userId || 0,
      batchId: item.batchId,
      comment: '',
      lpn: item.lpn
    }
  }

  const handleSubmit = () => {
    setLoading(true)

    const itemsToAdjust = addedItems.map((item, index) => ({ item, index }))

    // Mapeamos cada uno de los productos agregados para informar sobrantes.
    const payload = itemsToAdjust.map(({ index }) =>
      mapSingleItemToPayload(index)
    )

    // Si no contiene nungun registro se devuelve el mensaje.
    if (payload.length === 0) {
      handleSnackbar('No hay ajustes a informar.', true)
      setLoading(false)
      return
    }
    // Se declaran los sobrantes.
    callApi(postDeclareExcess(payload))
      .then(() => {
        handleSnackbar('Sobrantes informados correctamente', true)
        setItems([])
        setAddedItems([])
        requestGetInboundAdjust(pagination.page, pagination.pageSize)
        excessModalState.closeModal()
      })
      .catch((error) => {
        requestGetInboundAdjust(pagination.page, pagination.pageSize)
        handleSnackbar(error, false)
      })
      .finally(() => {
        setLoading(false)
        confirmModalState.closeModal()
      })
  }

  // Agregar elemento a la lista inferior y descuenta la cantidad
  // en caso de que no exista lo agrega.
  const handleAddItem = (item: WorkOrderInboundSkuAdjust) => {
    const quantityToAdd =
      quantities[
        `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}` ||
          0
      ]
    if (quantityToAdd > 0 && (item.excessQty || 0) === 0) {
      setAddedItems((prev) => {
        const existingItem = prev.find(
          (i) =>
            `${i.asnDetailId}_${i.sku}_${i.skuId}_${i.lpn}_${i.batchId}` ===
            `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
        )

        if (existingItem) {
          return prev.map((i) =>
            `${i.asnDetailId}_${i.sku}_${i.skuId}_${i.lpn}_${i.batchId}` ===
            `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
              ? { ...i, excessQty: quantityToAdd }
              : i
          )
        } else {
          return [...prev, { ...item, excessQty: quantityToAdd }]
        }
      })

      setQuantities((prev) => ({
        ...prev,
        [`${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`]: 0
      }))
    } else {
      // Si la cantidad es mayor a la disponible, puedes mostrar un mensaje de error
      handleSnackbar(
        'Las unidades a declarar no pueden ser igual a cero',
        false
      )
    }
  }

  const handleRemoveItem = (item: WorkOrderInboundSkuAdjust) => {
    setAddedItems((prev) =>
      prev.filter(
        (i) =>
          `${i.asnDetailId}_${i.sku}_${i.skuId}_${i.lpn}_${i.batchId}` !==
          `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
      )
    )

    handleSearch()
  }

  useEffect(() => {
    if (excessModalState.isOpen && workOrderId && workOrderId > 0) {
      requestGetInboundAdjust(pagination.page, pagination.pageSize)
    }
  }, [excessModalState.isOpen, workOrderId])

  // Manejar cambios en las cantidades
  const handleQuantityChange = (id: string, value: string) => {
    const quantity = parseInt(value, 10)

    // Verificar que la cantidad sea solo positiva
    if (quantity < 0) {
      return
    }

    // Aquí se verifica la cantidad restante
    setErrors((prevErrors) => ({
      ...prevErrors,
      [id]: false
    }))

    setQuantities((prev) => ({
      ...prev,
      [id]: isNaN(quantity) ? 0 : quantity
    }))
  }

  const handleSearchSkuChange = (sku: string) => {
    setFilterSku(sku)
    const skuId = items.find((i) => i.sku === sku)?.skuId
    setFilterSkuId(skuId || 0)
  }

  const handleSearchBatchChange = (batch: string) => {
    setFilterBatch(batch)
    const batchId = items.find((i) => i.batch === batch)?.batchId
    setFilterBatchId(batchId || 0)
  }

  const validateButtonLoad = () => {
    if (1 <= 0 || 1 <= 0) return true
    else return false
  }

  // si hay items agregados, no puede buscar
  const validateDisableSearch = () => {
    if (addedItems && addedItems.length > 0) return true
    else return false
  }

  // Limpia la busqueda
  const cleanSearch = () => {
    setFilterSku('')
    setFilterBatch('')
    setFilterSkuId(0)
    setFilterBatchId(0)
    requestGetInboundAdjust(pagination.page, pagination.pageSize)
  }

  // Permite buscar por SKU
  const handleSearch = () => {
    pagination.setPagination(0, pagination.pageSize)
    const filters: Filter[] = []

    if (filterSkuId !== 0) {
      filters.push({
        key: 'skuId',
        value: filterSkuId,
        label: 'Sku',
        displayValue: 'sku'
      })
    }
    if (filterBatchId !== 0) {
      filters.push({
        key: 'batchId',
        value: filterBatchId,
        label: 'Lote',
        displayValue: 'batch'
      })
    }

    requestGetInboundAdjust(0, pagination.pageSize, filters)
  }

  const handleClose = () => {
    setItems([])
    setAddedItems([])
    setFilterSku('')
    setFilterBatch('')
    setFilterSkuId(0)
    setFilterBatchId(0)
    excessModalState.closeModal()
  }

  const confirmModalState = useModal('Informar sobrantes')

  const showBatchInfo = items.some((item) => item.batchId > 0)
  const showLpnInfo = items.some((item) => (item.lpn?.trim() || '') !== '')
  const isLoading = state.status === Status.Loading || loading

  return (
    <Fragment>
      <Modal
        open={excessModalState.isOpen}
        title={excessModalState.title}
        onClose={handleClose}
        cancelButton={{
          title: 'Cerrar',
          action: handleClose
        }}
        fullWidth={true}
        maxWidth={'lg'}
      >
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh'
            }}
          >
            <Loading />
          </div>
        ) : (
          <Fragment>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              gap={2}
              className={classes.grid}
            >
              <FormControl>
                <TextField
                  size='small'
                  id='sku'
                  name='sku'
                  label='SKU'
                  value={filterSku}
                  onChange={(e: any) => handleSearchSkuChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                  variant='outlined'
                  placeholder='SKU a buscar...'
                />
              </FormControl>
              {showBatchInfo && (
                <FormControl>
                  <TextField
                    size='small'
                    id='batch'
                    name='batch'
                    label='Lote'
                    value={filterBatch}
                    onChange={(e: any) =>
                      handleSearchBatchChange(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                    variant='outlined'
                    placeholder='Lote a buscar...'
                  />
                </FormControl>
              )}
              <IconButton onClick={cleanSearch} size='medium'>
                <Refresh />
              </IconButton>
              <Button
                variant='contained'
                color='primary'
                size='medium'
                disabled={validateButtonLoad() || validateDisableSearch()}
                onClick={handleSearch}
              >
                Buscar
              </Button>
            </Box>
            <br />
            <Grid item xs={12} className={classes.grid}>
              <Grid
                item
                xs={12}
                justifyContent='center'
                alignItems='center'
                alignContent='center'
                style={{ textAlign: 'center' }}
              ></Grid>
              <Typography className={classes.title}>Disponibles</Typography>
              <Paper className={classes.paper}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableTitle}>
                          SKU
                        </TableCell>
                        {showBatchInfo ? (
                          <TableCell className={classes.tableTitle}>
                            Lote
                          </TableCell>
                        ) : (
                          ''
                        )}
                        {showLpnInfo ? (
                          <TableCell className={classes.tableTitle}>
                            LPN
                          </TableCell>
                        ) : (
                          ''
                        )}
                        <TableCell className={classes.tableTitle}>
                          Nombre Producto
                        </TableCell>
                        <TableCell className={classes.tableTitle}>
                          Venta En Verde
                        </TableCell>
                        <TableCell className={classes.tableTitle}>
                          Sobrantes
                        </TableCell>
                        <TableCell className={classes.tableTitle}>
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {items && items.length > 0 ? (
                      <TableBody>
                        {items.map((item) => {
                          if ((item.excessQty || 0) === 0) {
                            // Determinar si la fila debe estar deshabilitada
                            const isDisabled =
                              (addedItems.find(
                                (addedItem) =>
                                  addedItem.sku === item.sku &&
                                  addedItem.asnDetailId === item.asnDetailId &&
                                  addedItem.lpn === item.lpn &&
                                  addedItem.batchId === item.batchId
                              )?.excessQty || 0) > 0

                            return (
                              <TableRow
                                key={`${item.asnDetailId}_${item.skuId}_${item.lpn}_${item.batchId}`}
                                className={`${classes.tableRow} ${
                                  isDisabled ? classes.tableRowDisable : ''
                                }`}
                              >
                                <TableCell className={classes.tableCell}>
                                  {item.sku}
                                </TableCell>
                                {showBatchInfo ? (
                                  <TableCell className={classes.tableCell}>
                                    {item.batch}
                                  </TableCell>
                                ) : (
                                  ''
                                )}
                                {showLpnInfo ? (
                                  <TableCell className={classes.tableCell}>
                                    {item.lpn}
                                  </TableCell>
                                ) : (
                                  ''
                                )}
                                <TableCell className={classes.tableCell}>
                                  {item.name}
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  <StatusIcon value={item.isPreSaleReception} />
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  <FormControl>
                                    <TextField
                                      type='number'
                                      id='qty'
                                      name='qty'
                                      value={
                                        quantities[
                                          `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
                                        ] || 0
                                      }
                                      onChange={(e) =>
                                        handleQuantityChange(
                                          `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`,
                                          e.target.value
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          handleAddItem(item)
                                        }
                                      }}
                                      variant='outlined'
                                      placeholder='Ingrese Qty...'
                                      size='small'
                                      disabled={isDisabled}
                                      error={
                                        !!errors[
                                          `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
                                        ]
                                      }
                                      helperText={
                                        errors[
                                          `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
                                        ]
                                          ? 'El producto fue declarado con faltantes'
                                          : ''
                                      }
                                      InputProps={{
                                        style: {
                                          fontSize: '12px',
                                          padding: '3px'
                                        }
                                      }}
                                    />
                                  </FormControl>
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  <Button
                                    variant='contained'
                                    size='small'
                                    onClick={() => handleAddItem(item)}
                                    className={`${classes.button} ${classes.addButton}`}
                                    disabled={
                                      !quantities[
                                        `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
                                      ] ||
                                      errors[
                                        `${item.asnDetailId}_${item.sku}_${item.skuId}_${item.lpn}_${item.batchId}`
                                      ]
                                    }
                                  >
                                    Agregar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          } else {
                            return <TableRow></TableRow>
                          }
                        })}
                      </TableBody>
                    ) : (
                      <div>
                        <Typography className={classes.empty}>
                          {'No existe información'}
                        </Typography>
                      </div>
                    )}
                  </Table>
                </TableContainer>
              </Paper>

              <Pagination
                pagination={{
                  totalItem: state.data?.totalItems || 0,
                  page: pagination.page,
                  pageSize: pagination.pageSize
                }}
                onPaginationChange={handlePaginationChange}
              />
            </Grid>
            <br />
            <Grid item xs={12} className={classes.grid}>
              <Grid
                item
                xs={12}
                justifyContent='center'
                alignItems='center'
                alignContent='center'
                style={{ textAlign: 'center' }}
              >
                {addedItems && addedItems.length > 0 && (
                  <Tooltip
                    title='Declarar los productos agregados con unidades sobrantes'
                    placement='top-end'
                  >
                    <Button
                      className='btn-upload'
                      color='primary'
                      variant='contained'
                      component='span'
                      disabled={validateButtonLoad()}
                      onClick={() => confirmModalState.openModal('sm')}
                    >
                      Guardar
                    </Button>
                  </Tooltip>
                )}
              </Grid>
              <Typography variant='h6' className={classes.title}>
                Productos Agregados
              </Typography>
              <Paper className={classes.paper}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableTitle}>
                          SKU
                        </TableCell>
                        {showBatchInfo ? (
                          <TableCell className={classes.tableTitle}>
                            Lote
                          </TableCell>
                        ) : (
                          ''
                        )}
                        {showLpnInfo ? (
                          <TableCell className={classes.tableTitle}>
                            LPN
                          </TableCell>
                        ) : (
                          ''
                        )}
                        <TableCell className={classes.tableTitle}>
                          Nombre Producto
                        </TableCell>
                        <TableCell className={classes.tableTitle}>
                          Un. Sobrantes
                        </TableCell>
                        <TableCell className={classes.tableTitle}>
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {addedItems.map((addedItem, index) => (
                        <TableRow key={index} className={classes.tableRow}>
                          <TableCell className={classes.tableCell}>
                            {addedItem.sku}
                          </TableCell>
                          {showBatchInfo ? (
                            <TableCell className={classes.tableCell}>
                              {addedItem.batch}
                            </TableCell>
                          ) : (
                            ''
                          )}
                          {showLpnInfo ? (
                            <TableCell className={classes.tableCell}>
                              {addedItem.lpn}
                            </TableCell>
                          ) : (
                            ''
                          )}
                          <TableCell className={classes.tableCell}>
                            {addedItem.name}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {addedItem.excessQty}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Button
                              variant='contained'
                              size='small'
                              className={`${classes.button} ${classes.removeButton}`}
                              onClick={() => handleRemoveItem(addedItem)}
                            >
                              Quitar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}

                      {items.map((item) => {
                        if ((item.excessQty || 0) > 0) {
                          // Determinar si la fila debe estar deshabilitada
                          const isDisabled = (item.excessQty || 0) > 0
                          return (
                            <TableRow
                              key={`${item.asnDetailId}_${item.skuId}_${item.lpn}_${item.batchId}`}
                              className={classes.tableRow}
                            >
                              <TableCell className={classes.tableCell}>
                                {item.sku}
                              </TableCell>
                              {showBatchInfo ? (
                                <TableCell className={classes.tableCell}>
                                  {item.batch}
                                </TableCell>
                              ) : (
                                ''
                              )}
                              {showLpnInfo ? (
                                <TableCell className={classes.tableCell}>
                                  {item.lpn}
                                </TableCell>
                              ) : (
                                ''
                              )}
                              <TableCell className={classes.tableCell}>
                                {item.name}
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                {item.excessQty}
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                <Button
                                  variant='contained'
                                  size='small'
                                  className={`${classes.button} ${classes.removeButton}`}
                                  disabled={isDisabled}
                                >
                                  Quitar
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        } else {
                          return <TableRow></TableRow>
                        }
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Modal
              open={confirmModalState.isOpen}
              title={confirmModalState.title}
              maxWidth={confirmModalState.size}
              onClose={confirmModalState.closeModal}
              successButton={{
                action: () => handleSubmit(),
                title: 'Aceptar'
              }}
              cancelButton={{
                action: confirmModalState.closeModal,
                title: 'Salir'
              }}
            >
              <Fragment>
                <Typography
                  align='center'
                  variant='h6'
                  style={{ color: 'black' }}
                >
                  ¿Está seguro que desea declarar como sobrantes los ítems
                  seleccionados?
                </Typography>
                <br />
              </Fragment>
            </Modal>
          </Fragment>
        )}
      </Modal>
    </Fragment>
  )
}

const useStyles = makeStyles({
  container: {
    justifyContent: 'center'
  },
  grid: {
    margin: 'auto',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '8px'
  },
  paper: {
    maxHeight: 250,
    overflow: 'auto'
  },
  tableRow: {
    height: 25
  },
  tableRowDisable: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    pointerEvents: 'none'
  },
  tableCell: {
    textAlign: 'center',
    padding: '4px',
    fontSize: '12px'
  },
  tableTitle: {
    textAlign: 'center',
    padding: '6px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  button: {
    fontSize: '10px',
    minHeight: '20px',
    padding: '4px 8px'
  },
  removeButton: {
    backgroundColor: '#f48fb1',
    color: 'black'
  },
  addButton: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10vh',
    fontSize: '12px'
  }
})
